import type Service from '.'
import type {
  ChatSessionSummary,
  CreateThreadRequest,
  CreateThreadResponse,
  ListChatSessionMessagesResponse,
  ListChatSessionsRequest,
  ListChatSessionsResponse,
  RenameChatSessionRequest,
} from './transport/CopilotClient'

type CopilotStoreSnapshot = {
  isCreatingThread: boolean
  threadCreationError: string | null
}

export default class CopilotStore {
  private readonly listeners = new Set<() => void>()
  private readonly root: Service
  private readonly pendingThreadCreations = new Map<
    string | undefined,
    Promise<CreateThreadResponse>
  >()
  private snapshot: CopilotStoreSnapshot

  isCreatingThread = false
  threadCreationError: string | null = null

  constructor(root: Service) {
    this.root = root
    this.snapshot = this.createSnapshot()
  }

  createThread(
    { text, conversationId, mode }: CreateThreadRequest,
    chatKey?: string,
  ): Promise<CreateThreadResponse> {
    const pending = this.pendingThreadCreations.get(chatKey)
    if (pending) {
      return pending
    }

    this.setThreadCreationError(null)
    this.setIsCreatingThread(true)
    const request = this.root.transport.copilot.createThread({
      text,
      conversationId,
      mode,
    })
    const wrappedPromise = request
      .catch((error) => {
        this.setThreadCreationError('Unable to create thread. Please try again.')
        throw error
      })
      .finally(() => {
        this.pendingThreadCreations.delete(chatKey)
        this.setIsCreatingThread(this.pendingThreadCreations.size > 0)
      })

    this.pendingThreadCreations.set(chatKey, wrappedPromise)

    return wrappedPromise
  }

  submitToolApproval(
    sessionId: string,
    toolCallId: string,
    approved: boolean,
  ): Promise<void> {
    return this.root.transport.copilot.submitToolApproval(sessionId, toolCallId, approved)
  }

  clearThreadCreationError = () => {
    if (this.threadCreationError !== null) {
      this.setThreadCreationError(null)
    }
  }

  stopChatSession = ({ sessionId }: { sessionId: string }): Promise<void> => {
    return this.root.transport.copilot.stopChatSession({ sessionId })
  }

  deleteThread = ({ agentSessionId }: { agentSessionId: string }): Promise<void> => {
    return this.root.transport.copilot.deleteThread({ agentSessionId })
  }

  renameSession = ({
    agentSessionId,
    title,
  }: RenameChatSessionRequest): Promise<ChatSessionSummary> => {
    return this.root.transport.copilot.renameSession({ agentSessionId, title })
  }

  listChatSessions = (
    request: ListChatSessionsRequest = {},
  ): Promise<ListChatSessionsResponse> => {
    return this.root.transport.copilot.listChatSessions(request)
  }

  getChatSession = ({
    sessionId,
  }: {
    sessionId: string
  }): Promise<ChatSessionSummary> => {
    return this.root.transport.copilot.getChatSession({ sessionId })
  }

  getSessionMessages = ({
    sessionId,
    cursor,
  }: {
    sessionId: string
    cursor?: string
  }): Promise<ListChatSessionMessagesResponse> => {
    return this.root.transport.copilot.listChatSessionMessages({ sessionId, cursor })
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  getSnapshot = () => {
    return this.snapshot
  }

  private setThreadCreationError(value: string | null) {
    if (this.threadCreationError === value) {
      return
    }
    this.threadCreationError = value
    this.updateSnapshot()
    this.notify()
  }

  private setIsCreatingThread(value: boolean) {
    if (this.isCreatingThread === value) {
      return
    }

    this.isCreatingThread = value
    this.updateSnapshot()
    this.notify()
  }

  private updateSnapshot() {
    this.snapshot = this.createSnapshot()
  }

  private createSnapshot(): CopilotStoreSnapshot {
    return {
      isCreatingThread: this.isCreatingThread,
      threadCreationError: this.threadCreationError,
    }
  }

  private notify() {
    for (const listener of this.listeners) {
      listener()
    }
  }
}
